import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, Save } from '../../icons';
import { getOrgLeaders, saveOrgLeaders, type OrgLeaderDto } from '../../api/hr-org-leader';
// 빈 칸 세기 (2026-08-28) — 줄마다 「없음」이라 적어 두는 것만으로는 아무도 안 읽는다.
import { countLadderGaps, briefNames } from '../../lib/ladder-gaps';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { SaveBar } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import './AttendanceAdmin.css';

/**
 * 화면 S — **대표 · 부서장 지정**. 근태 5단계, 2026-08-24.
 *
 * 설계: `docs/attendance-round2-sample.html` 제안 3 · 화면 H·S.
 *
 * ⚠⚠ **2026-09-04 — 이 화면은 더 이상 결재선을 정하지 않는다** (판 P1).
 *
 *    사용자 결정: *「조직 사다리로 하지 말고 결재선을 사용자가 만들어서 사용하도록 한다.」*
 *    대표·부서장은 **조직의 책임자**이고, 그 값이 여는 것은 「우리 조직」 범위 · 업무보고 자격 ·
 *    인사 홈의 조직 지표다. 결재선은 전자결재 › **기본 결재선**에서 회사가 깔아 둔다.
 *    근거: `docs/design/adr-20260904-approval-line-user-composed.md`
 *
 * ★ **대표는 반드시 하나** — 마이그레이션이 온보딩 관리자로 채워 뒀고, 여기서 바꾼다.
 * ★ 부서장은 **직책과 무관하게 사람만** 고른다.
 * ★ 팀장은 **조직도의 것**이라 여기서 바꾸지 않는다 — 두 곳에서 고치면 조직도와 갈린다.
 *
 * ★★ **빈 칸을 센다** (2026-08-28). 사다리는 빈 자리를 건너뛰고 위로 가는데, 그 사실을
 *    **아무도 모른 채** 결재가 한 칸씩 짧아진다. 줄마다 「없음 — 이 단계를 건너뜁니다」 라고
 *    적어 두긴 했지만 팀이 서른 개면 아무도 다 읽지 않는다. 그래서 맨 위에서 **세어서**
 *    말한다 — 「팀 5개」보다 **「5개 팀 37명」** 이 사람을 움직인다.
 */

const ERRORS: Record<string, string> = {
  USER_NOT_FOUND: '이 회사에 없는 사용자가 있습니다.',
  DIVISION_NOT_FOUND: '이 회사에 없는 부서가 있습니다.',
  FORBIDDEN: '이 화면은 회사 관리자만 쓸 수 있습니다.',
};

export function OrgLeaderPage(): JSX.Element {
  const [data, setData] = useState<OrgLeaderDto | null>(null);
  const [rep, setRep] = useState('');
  const [divisionDraft, setDivisionDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /** 사다리의 빈 칸 — 저장한 값이 아니라 **지금 받아 온 조직**에서 센다. */
  const gaps = countLadderGaps(data);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getOrgLeaders();
      setData(d);
      setRep(d.representative?.userId ?? '');
      setDivisionDraft({});
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '조직 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  const changed = data !== null && (
    rep !== (data.representative?.userId ?? '') || Object.keys(divisionDraft).length > 0
  );

  const onSave = async () => {
    if (!data) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await saveOrgLeaders({
        representativeUserId: rep || null,
        divisions: Object.entries(divisionDraft).map(([id, leaderUserId]) => ({ id, leaderUserId: leaderUserId || null })),
      });
      /*
        ⛔ **종전 문구는 «거짓»이었다** (2026-09-27 에 고침):
             「다음 기안부터 이 사다리로 결재선이 만들어집니다」
           2026-09-04 판 P1 이후 **결재선은 이 사다리로 만들지 않는다** — 기안자가 고르거나
           회사 기본 결재선이 배치된다. 같은 화면이 두 자리에서 이미 그렇게 말하고 있었다:
             · 규칙 절   「결재선은 여기서 정하지 않습니다 — 전자결재 › 기본 결재선에서…」
             · 이 파일의 판 P1 주석 「…라고 말하면 «거짓말»이 된다」
           ⇒ **저장 성공 문구만 옛 말로 남아** 셋이 서로 어긋나 있었다.
        ★ 그래서 «이 저장이 실제로 여는 것»만 적는다 — 보는 범위와 업무보고 메뉴다(규칙 절과 같은 말).
      */
      setDone('저장했습니다 — 「우리 조직」에서 보는 범위와 업무보고 메뉴가 이 책임자 기준으로 바뀝니다.');
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hra">
      {/* ★ 제목은 **메뉴 이름과 같아야 한다** — 인사 트리에서 이 화면을 여는 사람은
              메뉴에 적힌 이름을 보고 누른다(2026-08-27 사용자 지적). */}
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/org-leaders')}
        scope={scopeFor('/erp/hr/org-leaders')}
        title="대표 · 부서장"
        description="「우리 조직」 범위와 업무보고 자격이 여기서 정해집니다"
      />

      <section className="hra__rule" aria-label="책임자">
        <h2 className="hra__rulehead">조직의 책임자를 정합니다</h2>
        <ul className="hra__rulelist">
          <li>여기서 정한 사람이 <b>「우리 조직」에서 보는 범위</b>를 갖습니다 — 팀장은 팀, 부서장은 부서, 대표는 전사.</li>
          <li>대표는 <b>업무보고</b> 메뉴를 봅니다 — 관리자 역할이 아니어도 됩니다.</li>
          <li>팀장은 <b>조직도</b>에서 정합니다 — 여기서는 보여만 줍니다(두 곳에서 고치면 갈립니다).</li>
          <li><b>결재선은 여기서 정하지 않습니다</b> — 전자결재 › 기본 결재선에서 회사가 깔아 둡니다.</li>
        </ul>
      </section>

      {/*
        ★★ 빈 칸 — **고치라고 재촉하는 자리가 아니라 «지금 이렇게 돌고 있다»를 말하는 자리**다.
           비워 두는 것도 회사의 선택일 수 있으므로 막지 않는다. 다만 모르고 있으면 안 된다.
      */}
      {gaps.any && (
        <section className="hra__gaps" aria-label="사다리 빈 칸">
          <p className="hra__gapshead">
            <AlertCircle size={14} aria-hidden />
            지금 조직에는 사다리의 빈 칸이 있습니다 — 그 단계는 <b>건너뜁니다</b>.
          </p>
          <ul className="hra__rulelist">
            {gaps.noRepresentative && (
              <li><b>대표가 없습니다</b> — 사다리의 끝이라 그 위가 없습니다.</li>
            )}
            {gaps.divisionsWithoutLeader.length > 0 && (
              <li>
                부서장이 빈 부서 <b>{gaps.divisionsWithoutLeader.length}개</b>
                <span className="hra__dim"> — {briefNames(gaps.divisionsWithoutLeader)}</span>
              </li>
            )}
            {gaps.teamsWithoutLeader.length > 0 && (
              <li>
                팀장이 빈 팀 <b>{gaps.teamsWithoutLeader.length}개</b>
                {gaps.affectedByTeamGap > 0 && <> · <b>{gaps.affectedByTeamGap}명</b></>}
                <span className="hra__dim"> — {briefNames(gaps.teamsWithoutLeader.map((t) => t.name))}</span>
              </li>
            )}
            {/* ★★ 가장 나쁜 조합은 따로 말한다 — 팀장 한 칸이면 사실상 결재가 없는 것과 같다. */}
            {gaps.divisionsCollapsed.length > 0 && (
              <li className="hra__gapsbad">
                <b>{briefNames(gaps.divisionsCollapsed)}</b> 는 대표도 부서장도 없어
                <b> 팀장 한 칸</b>으로 끝납니다.
              </li>
            )}
          </ul>
          <p className="hra__note">
            빈 칸이 잘못된 것은 아닙니다 — 회사가 그렇게 정했을 수 있습니다. 다만
            <b> 모르고 그렇게 도는 것</b>은 다릅니다.
          </p>
        </section>
      )}

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && !error && <p className="hra__ok" role="status"><Check size={14} aria-hidden />{done}</p>}

      <div className="hra__split">
        <aside className="hra__aside" aria-label="대표">
          <h2 className="hra__rulehead">대표 <span className="hra__dim">사다리의 끝 · 반드시 하나</span></h2>
          <label className="hra__field">
            <span>대표</span>
            <select value={rep} onChange={(e) => setRep(e.target.value)} disabled={loading}>
              <option value="">미지정</option>
              {(data?.users ?? []).map((u) => (
                <option key={u.userId} value={u.userId}>{u.name}{u.position ? ` · ${u.position}` : ''}</option>
              ))}
            </select>
          </label>
          {data?.representativeName && (
            <p className="hra__note">
              회사 정보의 대표자명은 <b>{data.representativeName}</b> 입니다 — 그것은 표시용 문자열이라
              사람을 가리키지 못합니다. 「우리 조직」 범위와 업무보고 자격은 위에서 고른 <b>계정</b>으로 갑니다.
            </p>
          )}

          {/*
            ⚠⚠ **2026-09-04 — 결재선 미리보기를 걷어냈다** (판 P1).
               대표·부서장은 «결재 사다리의 칸»이 아니게 됐으므로, 이 자리가
               「지금 조직이면 결재가 이렇게 됩니다」라고 말하면 **거짓말**이 된다.
               이 값들이 무엇을 여는지는 **화면 머리의 규칙 절**이 한 번만 말한다
               (같은 말을 두 자리에서 하면 한쪽만 고쳐지는 날이 온다).
          */}
        </aside>

        <section className="hra__main">
          <div className="hra__tablewrap">
            <table className="hra__table">
              <caption className="hra__caption">부서 {data?.divisions.length ?? 0}개 · 부서장은 직책과 무관하게 사람만 고릅니다</caption>
              <thead><tr><th scope="col">부서</th><th scope="col">부서장</th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan={2} className="hra__empty">불러오는 중…</td></tr>}
                {!loading && (data?.divisions.length ?? 0) === 0 && (
                  <tr><td colSpan={2} className="hra__empty">부서가 없습니다 — 팀장 다음이 곧 대표입니다.</td></tr>
                )}
                {(data?.divisions ?? []).map((d) => (
                  <tr key={d.id} className={d.id in divisionDraft ? 'is-changed' : undefined}>
                    <th scope="row">{d.name}</th>
                    <td>
                      <select
                        aria-label={`${d.name} 부서장`}
                        value={d.id in divisionDraft ? divisionDraft[d.id] : (d.leaderUserId ?? '')}
                        onChange={(e) => setDivisionDraft({ ...divisionDraft, [d.id]: e.target.value })}
                      >
                        <option value="">없음 — 이 단계를 건너뜁니다</option>
                        {(data?.users ?? []).map((u) => (
                          <option key={u.userId} value={u.userId}>{u.name}{u.position ? ` · ${u.position}` : ''}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="hra__tablewrap">
            <table className="hra__table">
              <caption className="hra__caption">팀 {data?.teams.length ?? 0}개 · 팀장은 조직도에서 정합니다(여기서는 보기만)</caption>
              <thead><tr><th scope="col">팀</th><th scope="col">팀장</th></tr></thead>
              <tbody>
                {(data?.teams ?? []).length === 0 && <tr><td colSpan={2} className="hra__empty">팀이 없습니다.</td></tr>}
                {(data?.teams ?? []).map((t) => (
                  <tr key={t.id}>
                    <th scope="row">{t.name}</th>
                    <td className={t.leaderName ? undefined : 'is-muted'}>
                      {t.leaderName ?? '없음 — 이 단계를 건너뜁니다'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 저장 줄 한 벌(2026-10-18 · web 레인 W8) — 종전 `hra__foot` 의 안내 · 되돌리기 · 저장을 글 · 조건 그대로 SaveBar 에 */}
          <SaveBar
            note="변경은 감사에 남습니다 · 이미 올라간 문서의 결재선은 바뀌지 않습니다"
            dirty={changed}
            onRevert={() => { setRep(data?.representative?.userId ?? ''); setDivisionDraft({}); }}
            canRevert={changed && !busy}
            canSave={changed && !busy}
            onSave={() => { void onSave(); }}
            saving={busy}
            saveIcon={<Save size={14} aria-hidden />}
            saveLabel={busy ? '저장 중…' : '저장'}
          />
        </section>
      </div>
    </div>
  );
}
